import polygonClipping from 'polygon-clipping'
import type { Pt2 } from './mercator'

/**
 * MapLibre keeps at most 500 rings per polygon (`EARCUT_MAX_RINGS` in its fill
 * bucket) and silently drops the smallest beyond that. A reverse oval over a
 * fragmented ground carries one hole per undissolved parcel, so past that cap
 * the dropped holes are legal hunting ground painted as no-hunting wash — the
 * one wrong thing this map must never show. Stay well under the cap so the
 * renderer never has to choose.
 */
export const MAX_RINGS_PER_POLYGON = 400

/**
 * Splits any polygon carrying more than `maxRings` rings by bisecting its
 * bounding box along its longer axis and intersecting each half, recursively,
 * until every piece is under the cap. Pieces abut exactly along the cut, so
 * their union is the original polygon; polygons under the cap pass through
 * untouched, which is every ordinary ground. Runs in projected metres, before
 * the conversion to lon/lat.
 */
export function capRingsPerPolygon(polys: readonly Pt2[][][], maxRings = MAX_RINGS_PER_POLYGON): Pt2[][][] {
  const out: Pt2[][][] = []
  const visit = (poly: Pt2[][], depth: number) => {
    if (poly.length <= maxRings || depth > 12) {
      out.push(poly)
      return
    }
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    for (const [x, y] of poly[0]) {
      if (x < minX) minX = x
      if (y < minY) minY = y
      if (x > maxX) maxX = x
      if (y > maxY) maxY = y
    }
    const vertical = maxX - minX >= maxY - minY
    const mid = vertical ? (minX + maxX) / 2 : (minY + maxY) / 2
    const pad = 1 // metre of slack so the cut rectangles enclose the bbox edges
    const halves: Pt2[][][] = vertical
      ? [
          [[[minX - pad, minY - pad], [mid, minY - pad], [mid, maxY + pad], [minX - pad, maxY + pad], [minX - pad, minY - pad]]],
          [[[mid, minY - pad], [maxX + pad, minY - pad], [maxX + pad, maxY + pad], [mid, maxY + pad], [mid, minY - pad]]],
        ]
      : [
          [[[minX - pad, minY - pad], [maxX + pad, minY - pad], [maxX + pad, mid], [minX - pad, mid], [minX - pad, minY - pad]]],
          [[[minX - pad, mid], [maxX + pad, mid], [maxX + pad, maxY + pad], [minX - pad, maxY + pad], [minX - pad, mid]]],
        ]
    for (const half of halves) {
      for (const piece of polygonClipping.intersection(poly, half)) visit(piece, depth + 1)
    }
  }
  for (const poly of polys) visit(poly, 0)
  return out
}
